import { useEffect, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { policyAreaLabel } from "@/components/profile/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Turns a member's sponsored bills into "what they actually work on" versus the chamber.
export const PolicyFocus = ({ bills, chamber: chamberRaw, selectedArea, onSelectArea, ScopeToggle }) => {
  const chamber = /senate/i.test(chamberRaw || "") ? "Senate" : "House";
  const [baseline, setBaseline] = useState(null);
  useEffect(() => {
    axios.get(`${API}/policy-areas/baseline`, { params: { chamber: chamber || "House" } })
      .then((r) => setBaseline(r.data)).catch(() => setBaseline({ shares: {} }));
  }, [chamber]);

  const counts = {};
  bills.forEach((b) => { const a = b.policyArea || "Unclassified"; counts[a] = (counts[a] || 0) + 1; });
  const total = bills.length || 1;
  const rows = Object.entries(counts).filter(([a]) => a !== "Unclassified").map(([area, n]) => {
    const share = n / total;
    const base = baseline?.shares?.[area] || 0;
    return { area, label: policyAreaLabel(area), n, share, base, ratio: base > 0 ? share / base : null };
  }).sort((a, b) => b.n - a.n);
  const top = rows.slice(0, 8);
  const maxShare = Math.max(...top.map((r) => Math.max(r.share, r.base)), 0.01);
  const heavy = rows.filter((r) => r.ratio != null && r.ratio >= 1.8 && r.n >= 2).slice(0, 2).map((r) => r.label);
  const light = Object.entries(baseline?.shares || {}).filter(([a, s]) => s >= 0.05 && !counts[a]).map(([a]) => policyAreaLabel(a)).slice(0, 2);
  const takeaway = !bills.length ? "No sponsored bills this Congress yet, so there is no portfolio to read."
    : heavy.length ? `Concentrates on ${heavy.join(" and ")}, well above the typical ${chamber || "House"} member${light.length ? `; no bills on ${light.join(" or ")}` : ""}.`
      : top.length ? `Spreads work across ${top.slice(0, 3).map((r) => r.label).join(", ")} in roughly the same mix as the chamber${light.length ? `; nothing yet on ${light.join(" or ")}` : ""}.` : "";

  return (
    <Card className="p-6 bg-white rounded-xl border-2 border-gray-200" data-testid="topic-distribution-card">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Policy Focus</h2>
          <p className="text-sm text-gray-600">What this member spends their legislative effort on, compared with a typical {chamber || "House"} member. Click a bar to filter the portfolio below.</p>
        </div>
        {ScopeToggle}
      </div>
      {takeaway && <p className="mt-3 text-[15px] text-gray-900 font-medium border-l-4 border-orange-400 pl-3" data-testid="policy-focus-takeaway">{takeaway}</p>}
      <div className="mt-5 space-y-2.5" data-testid="policy-focus-bars">
        {top.map((r) => {
          const active = selectedArea === r.area;
          return (
            <button key={r.area} type="button" onClick={() => onSelectArea(active ? null : r.area)} data-testid={`policy-focus-bar-${r.area}`}
              className={`w-full text-left rounded-lg px-3 py-2 transition-colors ${active ? "bg-orange-50 ring-1 ring-orange-300" : "hover:bg-gray-50"}`}>
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <span className="text-sm font-semibold text-gray-900 truncate">{r.label}</span>
                <span className="text-xs text-gray-600 shrink-0">
                  {r.n} bill{r.n === 1 ? "" : "s"} · {Math.round(r.share * 100)}% of portfolio
                  {r.ratio != null && <span className={`ml-2 font-bold ${r.ratio >= 1.5 ? "text-orange-600" : r.ratio <= 0.6 ? "text-gray-400" : "text-gray-700"}`}>{r.ratio.toFixed(1)}x average</span>}
                </span>
              </div>
              <div className="relative h-3 rounded bg-gray-100 overflow-hidden">
                <div className="absolute inset-y-0 left-0 rounded bg-orange-500/85" style={{ width: `${(r.share / maxShare) * 100}%` }} />
                {r.base > 0 && <div className="absolute inset-y-0 w-0.5 bg-white" style={{ left: `${(r.base / maxShare) * 100}%` }} title={`Typical ${chamber || "House"} member: ${Math.round(r.base * 100)}%`} />}
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-[11px] text-gray-500">Orange bar: share of this member's sponsored bills. White tick: share of all {chamber || "House"} bills this Congress in that area. Bills without a Congress.gov policy area are not counted.</p>
    </Card>
  );
};
